import { useState } from "react";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import { DateTime } from "luxon";
import { IconButton, InputAdornment, Popover, TextField } from "@mui/material";
import { faCalendarDays, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";

import { RootState } from "../../state/store";
import { monthOf, useShiftCalendar } from "../Hooks/useShiftCalendar";
import { useTouchFieldSize } from "../Hooks/useTouchLayout";
import { fromCalendar, toCalendar } from "./shiftCalendarTime";

export const ShiftCalendarField = ({ value, onChange, disabled = false, withTime = false, clearable = true, label, allowDate }: {
  value?: DateTime | null;
  onChange: (value: DateTime | null) => void;
  disabled?: boolean;
  withTime?: boolean;
  clearable?: boolean;
  label?: string;
  allowDate?: (date: DateTime) => boolean;
}) => {
  const { t } = useTranslation("drillBlast");
  const latestShiftDate = useSelector((state: RootState) => state.shift.latestShiftDate);
  const startingDate = () => value ?? (latestShiftDate ? DateTime.fromISO(latestShiftDate) : DateTime.now());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [viewedMonth, setViewedMonth] = useState<Date>(() => monthOf(startingDate()));
  const { isDateAllowed, renderDayContents, isFetching } = useShiftCalendar(viewedMonth, anchor != null);
  const size = useTouchFieldSize();

  const open = (target: HTMLElement) => {
    if (disabled) return;
    setViewedMonth(monthOf(startingDate()));
    setAnchor(target);
  };

  const selectable = (date: Date) =>
    (isFetching || isDateAllowed(date)) && (!allowDate || allowDate(fromCalendar(date, false)));

  const pick = (date: Date | null) => {
    if (!date) return;
    onChange(fromCalendar(date, withTime));
    if (!withTime) setAnchor(null);
  };

  return (
    <>
      <TextField
        size={size}
        fullWidth
        label={label}
        disabled={disabled}
        value={value ? value.toFormat(withTime ? "d MMM yyyy HH:mm" : "d MMM yyyy") : ""}
        onClick={e => open(e.currentTarget)}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                {value && clearable && !disabled && (
                  <IconButton size="small" aria-label={t("shiftCalendar.clear")} onClick={e => { e.stopPropagation(); onChange(null); }}>
                    <FontAwesomeIcon icon={faXmark} />
                  </IconButton>
                )}
                <FontAwesomeIcon icon={faCalendarDays} className="text-theme-text-muted" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Popover open={anchor != null} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <div className={`transition-opacity ${isFetching ? "opacity-60" : ""}`}>
          <DatePicker
            inline
            selected={toCalendar(value)}
            openToDate={viewedMonth}
            onChange={pick}
            renderDayContents={renderDayContents}
            filterDate={selectable}
            onMonthChange={setViewedMonth}
            onYearChange={setViewedMonth}
            showTimeSelect={withTime}
            timeFormat="HH:mm"
            timeIntervals={5}
            timeCaption={t("fields.time")}
          />
        </div>
      </Popover>
    </>
  );
};
